import type { ApolloCache, FetchResult } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import type {
  BasicJobInfoFragment,
  CreateAiDisplayImageMutation,
  CreateProjectMutation,
  DeleteProjectAssetMutation,
  DeleteProjectMutation,
  GetProjectQuery,
  GetProjectQueryVariables,
  GetProjectsQuery,
  GetProjectsQueryVariables,
} from 'src/shared/graphql/generated';
import {
  GetProjectDocument,
  GetProjectsDocument,
} from 'src/shared/graphql/generated';

export const createProjectCacheUpdater = (
  cache: ApolloCache<any>,
  mutationResult: FetchResult<CreateProjectMutation>,
) => {
  cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
    {
      query: GetProjectsDocument,
    },
    prevData => {
      if (!mutationResult?.data?.createProject) {
        return undefined;
      }

      const createdProject = mutationResult.data.createProject;
      const hasExistingProjects = !!prevData?.projects?.length;

      if (createdProject) {
        if (hasExistingProjects) {
          // If there is previous data, we clone it, and add the created project to the existing projects array
          const nextData = cloneDeep(prevData);
          nextData.projects?.push(createdProject);
          return nextData;
        }
        // If there is no previous data, we initialize the projects array with the created project
        return { projects: [createdProject] };
      }
      return undefined;
    },
  );
};

export const deleteProjectCacheUpdater = (
  cache: ApolloCache<any>,
  mutationResult: Omit<
    FetchResult<
      DeleteProjectMutation,
      Record<string, any>,
      Record<string, any>
    >,
    'context'
  >,
) => {
  cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
    {
      query: GetProjectsDocument,
    },
    prevData => {
      const deletedProject = mutationResult.data?.deleteProject;
      if (!deletedProject || !prevData?.projects) {
        return undefined;
      }

      const nextData = cloneDeep(prevData);
      if (nextData) {
        nextData.projects = prevData.projects.filter(
          ({ id }) => id !== deletedProject.id,
        );
        return nextData;
      }

      return undefined;
    },
  );
};

export const deleteProjectAssetCacheUpdater = (
  cache: ApolloCache<any>,
  mutationResult: FetchResult<DeleteProjectAssetMutation>,
) => {
  cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
    { query: GetProjectsDocument },
    prevData => {
      if (!mutationResult.data?.deleteProjectAsset || !prevData?.projects) {
        return undefined;
      }
      const { projectId, addableId } = mutationResult.data.deleteProjectAsset;
      const { projects } = prevData;

      const projectIndex = projects.findIndex(({ id }) => id === projectId);
      if (projectIndex === -1) return prevData;
      const project = projects[projectIndex];

      const nextData = cloneDeep(prevData);
      nextData.projects![projectIndex].addables = project.addables?.filter(
        ({ id }) => id !== addableId,
      );
      return nextData;
    },
  );
};
/**
 * Factory function to create a cache updater for creating a new project, and
 * adding it to the list of a users existing projects.
 *
 * @param variables Projects query variables (these need to match the
 * variables passed to the query you're updating)
 *
 * @returns function to be passed to the `update` option of the
 * `createAiDisplayImage` mutation
 */
export const createAiDisplayImageCacheUpdater =
  ({ projectIdToUpdate }: { projectIdToUpdate?: number }) =>
  (
    cache: ApolloCache<any>,
    mutationResult: Omit<
      FetchResult<
        CreateAiDisplayImageMutation,
        Record<string, any>,
        Record<string, any>
      >,
      'context'
    >,
  ) => {
    cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
      { query: GetProjectsDocument },
      prevData => {
        const createdAiDisplayImage =
          mutationResult?.data?.createAiDisplayImage;
        if (!createdAiDisplayImage || !prevData?.projects) {
          return undefined;
        }
        const projectIndex = prevData.projects.findIndex(
          project => project.id === projectIdToUpdate,
        );
        if (projectIndex > -1) {
          const nextData = cloneDeep(prevData);
          if (nextData?.projects?.[projectIndex].addables) {
            // If there are existing addables, we add the created
            // ai display image to the end of the array
            nextData.projects[projectIndex].addables?.push(
              createdAiDisplayImage,
            );
          } else if (nextData?.projects?.[projectIndex]) {
            // If there are no existing addables, we initialize the
            // addables array with the created ai display image
            nextData.projects[projectIndex].addables = [createdAiDisplayImage];
          }
          return nextData;
        }
        return undefined;
      },
    );
  };

export const attachScanToProjectUpdater =
  ({
    projectIdToUpdate,
    scan,
  }: {
    projectIdToUpdate?: number;
    scan: BasicJobInfoFragment;
  }) =>
  (cache: ApolloCache<any>) => {
    cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
      { query: GetProjectsDocument },
      prevData => {
        if (!prevData?.projects) {
          return undefined;
        }
        const projectIndex = prevData.projects.findIndex(
          project => project.id === projectIdToUpdate,
        );
        if (projectIndex > -1) {
          const nextData = cloneDeep(prevData);
          if (nextData?.projects?.[projectIndex].jobs) {
            // If there are existing addables, we add the created
            // ai display image to the end of the array
            nextData.projects[projectIndex].jobs?.push(scan);
          } else if (nextData?.projects?.[projectIndex]) {
            // If there are no existing addables, we initialize the
            // addables array with the created ai display image
            nextData.projects[projectIndex].jobs = [scan];
          }
          return nextData;
        }
        return undefined;
      },
    );

    if (!projectIdToUpdate) {
      return;
    }

    cache.updateQuery<GetProjectQuery, GetProjectQueryVariables>(
      {
        query: GetProjectDocument,
        variables: { projectId: projectIdToUpdate },
      },
      prevData => {
        if (!prevData?.project) {
          return undefined;
        }
        const nextData = cloneDeep(prevData);
        if (prevData.project.jobs?.length) {
          // If there are existing addables, we add the created
          // ai display image to the end of the array
          nextData.project?.jobs?.push(scan);
        } else if (nextData?.project) {
          // If there are no existing addables, we initialize the
          // addables array with the created ai display image
          nextData.project.jobs = [scan];
        }
        return nextData;
      },
    );
  };

export const updateScanToProjectUpdater =
  ({
    projectIdToUpdate,
    scan,
    removedScanId,
  }: {
    projectIdToUpdate?: number;
    scan: BasicJobInfoFragment;
    removedScanId: number;
  }) =>
  (cache: ApolloCache<any>) => {
    if (!projectIdToUpdate) {
      return;
    }

    cache.updateQuery<GetProjectQuery, GetProjectQueryVariables>(
      {
        query: GetProjectDocument,
        variables: { projectId: projectIdToUpdate },
      },
      prevData => {
        if (!prevData?.project) {
          return undefined;
        }
        const nextData = cloneDeep(prevData);
        if (nextData.project?.jobs?.length) {
          // If there are existing addables, we add the created
          // ai display image to the end of the array
          nextData.project.jobs = nextData.project?.jobs?.filter(
            job => job.id !== removedScanId,
          );
          nextData.project.jobs.unshift(scan);
        } else if (nextData?.project) {
          // If there are no existing addables, we initialize the
          // addables array with the created ai display image
          nextData.project.jobs = [scan];
        }
        return nextData;
      },
    );
  };

export const removeScanFromProjectUpdater =
  ({
    projectIdToUpdate,
    scan,
  }: {
    projectIdToUpdate?: number;
    scan: BasicJobInfoFragment;
  }) =>
  (cache: ApolloCache<any>) => {
    cache.updateQuery<GetProjectsQuery, GetProjectsQueryVariables>(
      { query: GetProjectsDocument },
      prevData => {
        if (!prevData?.projects) {
          return undefined;
        }
        const projectIndex = prevData.projects.findIndex(
          project => project.id === projectIdToUpdate,
        );
        if (projectIndex > -1) {
          const nextData = cloneDeep(prevData);
          if (nextData?.projects?.[projectIndex].jobs) {
            nextData.projects[projectIndex].jobs = nextData.projects[
              projectIndex
            ].jobs?.filter(job => job.id !== scan.id);
          }
          return nextData;
        }
        return undefined;
      },
    );

    if (!projectIdToUpdate) {
      return;
    }

    cache.updateQuery<GetProjectQuery, GetProjectQueryVariables>(
      {
        query: GetProjectDocument,
        variables: { projectId: projectIdToUpdate },
      },
      prevData => {
        if (!prevData?.project) {
          return undefined;
        }
        const nextData = cloneDeep(prevData);
        if (prevData.project?.jobs?.length) {
          nextData.project!.jobs = nextData.project?.jobs?.filter(
            job => job.id !== scan.id,
          );
        }
        return nextData;
      },
    );
  };
